import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Ban, Plus, Clock, MapPin, Mail, ScrollText } from "lucide-react";
import { PageBody, PageHeader } from "@/components/Page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";

export default function Compliance() {
  const [supp, setSupp] = useState([]);
  const [settings, setSettings] = useState(null);
  const [val, setVal] = useState("");
  const [addr, setAddr] = useState("");

  const load = async () => {
    const [s, c] = await Promise.all([api.get("/compliance/suppressions"), api.get("/compliance/settings")]);
    setSupp(s.data); setSettings(c.data); setAddr(c.data.physical_address || "");
  };
  useEffect(() => { load(); }, []);

  const add = async () => { if (!val) return; await api.post("/compliance/suppressions", { value: val }); setVal(""); load(); toast.success("Added to suppression list"); };
  const remove = async (id) => { await api.delete(`/compliance/suppressions/${id}`); load(); };
  const saveAddr = async () => { await api.put("/compliance/address", { physical_address: addr }); toast.success("Mailing address saved"); };

  return (
    <PageBody>
      <PageHeader eyebrow="Compliance Center" title="Compliant by construction"
        subtitle="CAN-SPAM guardrails, global suppression, sending windows, and an audit trail for generate / approve / send." />

      <div className="grid lg:grid-cols-3 gap-5 mb-6">
        {[
          { icon: Mail, label: "Unsubscribe link", val: "On every email", ok: true },
          { icon: Clock, label: "Sending window", val: settings?.sending_window || "-", ok: true },
          { icon: ShieldCheck, label: "Daily inbox cap", val: `${settings?.daily_inbox_cap || 0} / inbox`, ok: true },
        ].map((x) => (
          <div key={x.label} className="rounded-xl border border-border/70 bg-card/50 p-5">
            <x.icon className="h-5 w-5 text-emerald-500 mb-3" />
            <div className="text-xs text-muted-foreground">{x.label}</div>
            <div className="text-sm font-medium mt-0.5">{x.val}</div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border/70 bg-card/50 p-6">
          <div className="flex items-center gap-2 mb-1"><MapPin className="h-4 w-4 text-amber-500" /><h3 className="font-display text-lg font-semibold tracking-tight">Physical mailing address</h3></div>
          <p className="text-xs text-muted-foreground mb-4">Required by CAN-SPAM. Appended to every marketing email footer.</p>
          <Input value={addr} onChange={(e) => setAddr(e.target.value)} data-testid="mailing-address-input" className="mb-3" />
          <Button onClick={saveAddr} className="rounded-md" data-testid="save-address-btn">Save address</Button>
        </div>

        <div className="rounded-xl border border-border/70 bg-card/50 p-6">
          <div className="flex items-center gap-2 mb-1"><Ban className="h-4 w-4 text-rose-500" /><h3 className="font-display text-lg font-semibold tracking-tight">Global suppression list</h3></div>
          <p className="text-xs text-muted-foreground mb-4">Organization-scoped. Emails or domains here are never contacted.</p>
          <div className="flex gap-2 mb-4">
            <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder="email@domain.com or domain.com" data-testid="compliance-suppress-input" />
            <Button onClick={add} className="rounded-md shrink-0" data-testid="add-suppress-btn"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="space-y-1.5 max-h-52 overflow-y-auto">
            {supp.map((s) => (
              <div key={s.id} className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2 text-sm">
                <span className="font-mono text-xs">{s.value}</span>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground">{s.reason}</span>
                  <button onClick={() => remove(s.id)} className="text-muted-foreground hover:text-rose-500 text-xs">remove</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-xl border border-border/70 bg-card/50 p-6">
        <div className="flex items-center gap-2 mb-4"><ScrollText className="h-4 w-4 text-sky-500" /><h3 className="font-display text-lg font-semibold tracking-tight">Audit log</h3></div>
        <div className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="audit-log">
          {(settings?.audit_logs || []).length === 0 && <p className="text-sm text-muted-foreground">Generate, approve, and send events will appear here.</p>}
          {(settings?.audit_logs || []).map((a) => (
            <div key={a.id} className="flex items-center justify-between text-xs font-mono border-b border-border/40 last:border-0 py-1.5">
              <span><span className="text-amber-500">{a.action}</span> · {a.target}</span>
              <span className="text-muted-foreground">{a.user_name} · {new Date(a.at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" })}</span>
            </div>
          ))}
        </div>
      </div>
    </PageBody>
  );
}
